import { action } from '@ember/object';
import type RouterService from '@ember/routing/router';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask, task, type TaskInstance } from 'ember-concurrency';
import type DraftModel from 'garaje/models/draft';
import type AjaxService from 'garaje/services/ajax';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MapVersionsService from 'garaje/services/map-versions';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import urlBuilder from 'garaje/utils/url-builder';
import { defer } from 'rsvp';

// Interface for resource objects
interface Resource {
  title: string;
  type: string;
  geometryType: string;
  icon: string;
  disabled: boolean;
  tooltip?: string;
  beta?: boolean;
  canImportCSV?: boolean;
  onClick: (resource: Resource) => void;
}

// Interface for section objects
interface Section {
  title: string;
  resources: Resource[];
}

// Interface for category objects
interface Category {
  title: string;
  sections: Section[];
}

interface DeskCsvData {
  floorName: string;
  neighborhood: string;
  desk: string;
  enabled: string;
  assignedTo: string;
  amenities: string;
}

// Interface for component arguments
interface AvailableResourcesListViewArgs {
  categories: Category[];
  placingResource?: Resource;
  draft?: DraftModel;
  unplacedResources: Resource[];
  showUnplacedParkingPanel: boolean;
  setShowUnplacedParkingPanel: (show: boolean) => void;
  openUnplacedParkingPanel: () => void;
  refreshModel: () => void;
  setPlacingResource: (resource: Resource) => void;
  onSelectedFeatureChange: (feature: unknown) => void;
}

interface MyTaskInstance<T> extends TaskInstance<T> {
  abort: () => void;
  continue: () => void;
}

export default class AvailableResourcesListView extends Component<AvailableResourcesListViewArgs> {
  @service declare ajax: AjaxService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare store: StoreService;
  @service declare mapVersions: MapVersionsService;
  @service declare flashMessages: FlashMessagesService;
  @service declare metrics: MetricsService;
  @service declare router: RouterService;
  @service declare state: StateService;

  @tracked collapsedCategories: string[] = [];
  @tracked isResourceMenuOpen = false;
  @tracked isImportCSVModalOpen = false;
  @tracked file: File | null = null;
  @tracked records: DeskCsvData[] = [];

  @action
  setCollapsedCategories(category: string): void {
    if (this.collapsedCategories.includes(category)) {
      this.collapsedCategories = this.collapsedCategories.filter((collapsedCategory) => collapsedCategory !== category);
    } else {
      this.collapsedCategories = [...this.collapsedCategories, category];
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  uploadFileTask = task({ drop: true }, async (): Promise<void> => {
    const { id: locationId } = this.state.currentLocation;
    const uploadUrl = urlBuilder.rms.uploadDesksCsv(locationId, this.args.draft?.id);
    const formData = new FormData();

    if (this.file) {
      formData.append('file', this.file);
    }

    try {
      await this.ajax.request(uploadUrl, {
        contentType: false,
        data: formData,
        processData: false,
        type: 'POST',
      });

      this.flashMessages.showAndHideFlash('success', 'File uploaded!');
      this.metrics.trackEvent('Draft desks csv imported', { count: this.records.length });
      this.importCSVModalTask.last?.abort();
      this.args.refreshModel();
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.log('err', err);
      this.flashMessages.showAndHideFlash('error', 'There was an error with your csv', parseErrorForDisplay(err));
    }
  });

  @dropTask
  importCSVModalTask: {
    perform(): Generator<Promise<unknown>, unknown, unknown>;
    last?: MyTaskInstance<Promise<unknown>>;
  } = {
    *perform(this: { context: AvailableResourcesListView; abort: () => void; continue: () => void }) {
      const deferred = defer();
      this.abort = () => {
        this.context.isResourceMenuOpen = false;
        deferred.resolve(false);
      };
      this.continue = () => {
        this.context.isResourceMenuOpen = false;
        deferred.resolve(true);
      };
      return yield deferred.promise;
    },
  };

  get downloadCsvUrl(): string {
    return urlBuilder.rms.downloadDesksCsv(this.state.currentLocation.id, this.args.draft?.id);
  }

  @action
  handleResourceClick(resource: Resource): void {
    const {
      unplacedResources,
      setShowUnplacedParkingPanel,
      openUnplacedParkingPanel,
      showUnplacedParkingPanel,
      setPlacingResource,
      onSelectedFeatureChange,
    } = this.args;

    if (
      resource.type === 'parking' &&
      typeof openUnplacedParkingPanel === 'function' &&
      !showUnplacedParkingPanel &&
      this.featureFlags.isEnabled('add-unplaced-parking') &&
      unplacedResources.length > 0
    ) {
      openUnplacedParkingPanel();
    } else if (showUnplacedParkingPanel) {
      // Close the unplaced parking panel
      setShowUnplacedParkingPanel(false);

      // Reset the placing resource to a generic parking resource so clicking on map creates new parking spots
      if (setPlacingResource) {
        const genericParkingResource: Resource = {
          type: 'parking',
          title: 'Parking',
          geometryType: 'Point',
          icon: 'parking',
          beta: true,
          disabled: false,
          tooltip: 'Parking',
          onClick: (_resource: Resource) => {
            // This will be handled by the placeResource method
          },
        };
        setPlacingResource(genericParkingResource);
      }

      // Close any open resource forms
      if (onSelectedFeatureChange) {
        onSelectedFeatureChange(null);
      }
    }

    resource.onClick(resource);
  }

  @action
  onResourceMenuClick(e: Event): void {
    e.stopPropagation();
    this.isResourceMenuOpen = !this.isResourceMenuOpen;
  }

  @action
  onImportCSVClick(e: Event): void {
    e.stopPropagation();
    this.isImportCSVModalOpen = true;
  }
}
